import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, ArrowLeft, Briefcase, Clock, MapPin, Laptop, 
  Heart, DollarSign, Shield, Sparkles, Users, Star, ChevronDown,
  Monitor, Calendar, Gift, Zap, ArrowRight
} from 'lucide-react';
import ApplicationForm from '../components/ApplicationForm';
import { usePageMeta } from '../hooks/usePageMeta';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Animated Counter
const AnimatedCounter = ({ value, suffix = "", prefix = "" }) => {
  const [count, setCount] = React.useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  React.useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value.toString().replace(/[^0-9]/g, ''));
      const duration = 1500;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Benefit Card Component
const BenefitCard = ({ icon: Icon, title, description, color, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
    >
      <motion.div
        whileHover={{ rotate: [0, -10, 10, 0] }}
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)` }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </motion.div>
      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1E3A8A] transition-colors">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </motion.div>
  );
};

// Role Card Component
const RoleCard = ({ role, featured, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const isFilled = role.filled;
  const isHiring = role.hiring;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      whileHover={{ y: isHiring ? -8 : -4 }}
      className={`relative rounded-2xl p-8 overflow-hidden ${
        isHiring && featured 
          ? 'bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] text-white shadow-2xl' 
          : isFilled 
            ? 'bg-gray-50 border-2 border-gray-200 shadow-md'
            : 'bg-white border-2 border-gray-100 shadow-lg'
      }`}
    >
      {/* Position Filled Ribbon */}
      {isFilled && (
        <div className="absolute top-5 -right-8 rotate-45 bg-gray-500 text-white text-xs font-bold py-1 px-10 shadow-md">
          FILLED
        </div>
      )}

      {/* Now Hiring Badge */}
      {isHiring && featured && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1 z-10"
        >
          <Zap className="w-3 h-3" /> Now Hiring
        </motion.div>
      )}
      
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${
        isHiring && featured ? 'bg-white/20 text-white' : isFilled ? 'bg-gray-200 text-gray-600' : 'bg-[#1E3A8A]/10 text-[#1E3A8A]'
      }`}>
        <Briefcase className="w-3 h-3" />
        {role.type}
      </div>
      
      <h3 className={`text-2xl font-bold mb-2 ${isHiring && featured ? 'text-white' : isFilled ? 'text-gray-700' : 'text-gray-900'}`}>
        {role.title}
      </h3>
      
      <div className={`text-3xl font-bold mb-4 ${isHiring && featured ? 'text-amber-300' : isFilled ? 'text-gray-500' : 'text-[#1E3A8A]'}`}>
        {role.salary}
      </div>
      
      <p className={`mb-6 leading-relaxed ${isHiring && featured ? 'text-blue-100' : isFilled ? 'text-gray-500' : 'text-gray-600'}`}>
        {role.description}
      </p>
      
      <ul className="space-y-3 mb-6">
        {role.highlights.map((item, idx) => (
          <motion.li
            key={idx}
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 + idx * 0.1 }}
            className="flex items-start gap-3 text-sm"
          >
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${isHiring && featured ? 'text-amber-300' : isFilled ? 'text-gray-400' : 'text-green-500'}`} />
            <span className={isHiring && featured ? 'text-blue-50' : isFilled ? 'text-gray-500' : 'text-gray-700'}>{item}</span>
          </motion.li>
        ))}
      </ul>
      
      {isHiring ? (
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
          className={`w-full py-3 px-6 rounded-xl font-semibold transition-all ${
            featured 
              ? 'bg-white text-[#1E3A8A] hover:bg-gray-100' 
              : 'bg-[#1E3A8A] text-white hover:bg-[#1E3A8A]/90'
          }`}
        >
          Apply Now <ArrowRight className="w-4 h-4 inline ml-2" />
        </motion.button>
      ) : (
        <div className="w-full py-3 px-6 rounded-xl font-semibold text-center bg-gray-200 text-gray-500 cursor-not-allowed">
          Position Filled
        </div>
      )}
    </motion.div>
  );
};


// Floating Badge Component
const FloatingBadge = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.4 }}
    className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-100"
  >
    {children}
  </motion.div>
);

const ApplicationsPage = () => {
  usePageMeta({
    title: 'Join the BM Executive Team | Virtual Assistant Careers',
    description: 'Join BM-Assist as an Executive or Administrative Assistant. Work remotely with competitive pay ($60K-$115K), full Apple equipment, and support real small businesses.',
    ogTitle: 'Join the BM Executive Team | Virtual Assistant Careers',
    ogDescription: 'Remote executive assistant positions with competitive compensation, latest Apple technology, and the opportunity to support growing small businesses.'
  });

  const benefits = [
    { icon: Calendar, title: "Paid Holidays & PTO", description: "Enjoy time off with 10+ paid holidays and flexible PTO to rest and recharge.", color: "#1E3A8A" },
    { icon: Heart, title: "Health, Dental, Vision", description: "Comprehensive health coverage options for you and your family.", color: "#EF4444" },
    { icon: DollarSign, title: "401(k) Retirement", description: "Available after eligibility period to help you save for the future.", color: "#10B981" },
    { icon: Laptop, title: "Apple Equipment", description: "Latest MacBook, iPad, and accessories provided for full-time hires.", color: "#8B5CF6" },
    { icon: MapPin, title: "100% Remote", description: "Work from anywhere with flexible hours and async communication.", color: "#F59E0B" },
    { icon: Users, title: "Small Team Culture", description: "Direct access to leadership and real impact on company direction.", color: "#EC4899" },
  ];

  const roles = [
    {
      title: "Contract / As-Needed",
      type: "Project-Based",
      salary: "Competitive Hourly",
      description: "Flexible project-based work for specific tasks or overflow support during busy periods.",
      highlights: [
        "Flexible schedule",
        "Choose your projects",
        "Set your own hours",
        "Paid per project"
      ],
      featured: true,
      hiring: true
    },
    {
      title: "Marketing Specialist",
      type: "Full-Time",
      salary: "$80K – $115K/yr",
      description: "The hands-on creator role. You'll design business cards, set up Google profiles, create flyers, and deliver the marketing materials our clients need.",
      highlights: [
        "Design print materials (cards, flyers, signs)",
        "Set up Google Business & social profiles",
        "Create social media graphics & templates",
        "Execute ad campaigns & landing pages"
      ],
      filled: true
    },
    {
      title: "Executive Assistant",
      type: "Full-Time",
      salary: "$60K – $90K/yr",
      description: "Senior support role working directly with the executive team on high-priority tasks and confidential matters.",
      highlights: [
        "Manage executive inbox and calendar",
        "Light project coordination",
        "Handle confidential information",
        "Travel and expense management"
      ],
      filled: true
    },
    {
      title: "Personal / Admin Assistant",
      type: "Full-Time",
      salary: "$60K – $75K/yr",
      description: "Virtual assistant supporting day-to-day operations with scheduling, follow-ups, and research tasks.",
      highlights: [
        "Calendar & scheduling management",
        "Vendor calls and follow-ups",
        "Light research and data entry",
        "Personal-business crossover tasks"
      ],
      filled: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
              BM-Assist.co
            </Link>
            <Link to="/">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full filter blur-3xl opacity-30"
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-0 right-10 w-96 h-96 bg-purple-200 rounded-full filter blur-3xl opacity-20"
            animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Zap className="w-4 h-4" />
              We're Hiring
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              <span className="text-gray-900">Join the </span>
              <span className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                BM Executive Team
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed"
            >
              Fully remote. Competitive pay. Apple equipment provided. Join a small team making a big impact 
              for local businesses across the country.
            </motion.p>

            {/* Floating badges */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-3 mb-10"
            >
              <FloatingBadge delay={0.5}>
                <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                <span className="text-sm font-medium text-gray-700">100% Remote</span>
              </FloatingBadge>
              <FloatingBadge delay={0.6}>
                <Clock className="w-4 h-4 text-[#1E3A8A]" />
                <span className="text-sm font-medium text-gray-700">Flexible Hours</span>
              </FloatingBadge>
              <FloatingBadge delay={0.7}>
                <Laptop className="w-4 h-4 text-[#1E3A8A]" />
                <span className="text-sm font-medium text-gray-700">Equipment Provided</span>
              </FloatingBadge>
            </motion.div>

            {/* CTA */}
            <motion.div variants={fadeInUp}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('roles')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#1E3A8A] text-white text-lg px-8 py-4 rounded-full shadow-xl shadow-blue-500/30 font-semibold inline-flex items-center gap-2"
              >
                View Open Positions
                <ChevronDown className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-[#1E3A8A] mb-1">
                <AnimatedCounter value="100" suffix="%" />
              </div>
              <p className="text-gray-600 text-sm">Remote Work</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#1E3A8A] mb-1">
                <AnimatedCounter prefix="$" value="60" suffix="K+" />
              </div>
              <p className="text-gray-600 text-sm">Starting Salary</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#1E3A8A] mb-1">
                <AnimatedCounter value="10" suffix="+" />
              </div>
              <p className="text-gray-600 text-sm">Paid Holidays</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#1E3A8A] mb-1">
                <AnimatedCounter value="1" />
              </div>
              <p className="text-gray-600 text-sm">Open Positions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-[#1E3A8A]/10 text-[#1E3A8A] rounded-full text-sm font-semibold mb-4"
            >
              <Gift className="w-4 h-4 inline mr-2" />
              Full-Time Benefits
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Real roles, <span className="text-[#1E3A8A]">not gigs</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              Full-time BM-Assist roles come with comprehensive benefits, not just a paycheck.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <BenefitCard key={benefit.title} {...benefit} delay={index * 0.1} />
            ))}
          </div>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-sm text-gray-500 italic mt-8"
          >
            * Contract/as-needed roles are paid per project but do not include benefits.
          </motion.p>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
            >
              <Briefcase className="w-4 h-4 inline mr-2" />
              Open Positions
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Find your <span className="text-[#1E3A8A]">role</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              Pay ranges depend on experience, tools you know, and how independently you can work.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {roles.map((role, index) => (
              <RoleCard key={role.title} role={role} featured={role.featured} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.span 
              className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4"
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Apply Now
            </motion.span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ready to <span className="text-[#1E3A8A]">get started?</span>
            </h2>
            <p className="text-lg text-gray-600">
              Fill out the application below and we'll be in touch within 48 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
          >
            <ApplicationForm />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                BM-Assist.co
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} BM-Assist.co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ApplicationsPage;
